import { Button } from "@design-system/Inputs/Button";
import { FlexCol } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { type FC } from "react";
import { useSupplierQuestionnaireSendModalTranslations } from "./i18n";

export const SendQuestionnaireModal: FC<{
    notAnsweredQuestionsCount: number;
    notValidatedQuestionsCount: number;
    missingProofQuestionsCount: number;
    loading: boolean;
    onConfirm: () => void;
}> = ({
    notAnsweredQuestionsCount,
    notValidatedQuestionsCount,
    missingProofQuestionsCount,
    loading,
    onConfirm,
}) => {
    const { t, keys } = useSupplierQuestionnaireSendModalTranslations();

    const isComplete =
        !notAnsweredQuestionsCount && !missingProofQuestionsCount;

    return (
        <Modal>
            <Modal.Trigger asChild>
                <Button>{t(keys.modal_trigger_button)}</Button>
            </Modal.Trigger>
            <Modal.Overlay scrollable centerContent>
                <Modal.Content>
                    <Modal.Header withClose>
                        <Modal.Title>{t(keys.modal_title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <Text variant="body" color="primary">
                                {t(keys.modal_main_content)}
                            </Text>
                            {!!notAnsweredQuestionsCount && (
                                <FlexCol
                                    p="4"
                                    br="lg"
                                    className="border border-warning-300 bg-warning-100"
                                >
                                    <Text
                                        variant="body-sm-bold"
                                        color="primary"
                                        className="uppercase"
                                    >
                                        {t(keys.modal_warning_title)}
                                    </Text>
                                    <Text variant="body" color="primary">
                                        {t(
                                            keys.modal_not_answered_questions_warning,
                                            {
                                                count: notAnsweredQuestionsCount,
                                            },
                                        )}
                                    </Text>
                                </FlexCol>
                            )}
                            {!!notValidatedQuestionsCount && (
                                <FlexCol
                                    p="4"
                                    br="lg"
                                    className="border border-warning-300 bg-warning-100"
                                >
                                    <Text
                                        variant="body-sm-bold"
                                        color="primary"
                                        className="uppercase"
                                    >
                                        {t(keys.modal_warning_title)}
                                    </Text>
                                    <Text variant="body" color="primary">
                                        {t(
                                            keys.modal_not_validated_questions_warning,
                                            {
                                                count: notValidatedQuestionsCount,
                                            },
                                        )}
                                    </Text>
                                </FlexCol>
                            )}
                            {!!missingProofQuestionsCount && (
                                <FlexCol
                                    p="4"
                                    br="lg"
                                    className="border border-danger bg-red-100"
                                >
                                    <Text
                                        variant="body-sm-bold"
                                        color="primary"
                                        className="uppercase"
                                    >
                                        {t(keys.modal_warning_title)}
                                    </Text>
                                    <Text variant="body" color="primary">
                                        {t(
                                            keys.modal_missing_proof_questions_warning,
                                            {
                                                count: missingProofQuestionsCount,
                                            },
                                        )}
                                    </Text>
                                </FlexCol>
                            )}
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>
                            {t(keys.modal_cancel_button)}
                        </Modal.CancelCTA>
                        <Modal.ConfirmCTA
                            variant={isComplete ? "flatPrimary" : "flatDanger"}
                            loading={loading}
                            onClick={onConfirm}
                        >
                            {t(keys.modal_confirm_button)}
                        </Modal.ConfirmCTA>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
